import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import sum from 'lodash-es/sum';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum GuestInvoiceStatus {
  open = 'open',
  paymentError = 'payment_error',
  paymentMethodMissing = 'payment_method_missing',
  markedAsPaid = 'marked_as_paid',
  paid = 'paid',
  void = 'void',
}

const PAID_STATUSES = [
  GuestInvoiceStatus.paid,
  GuestInvoiceStatus.markedAsPaid,
  GuestInvoiceStatus.void,
];

export default class GuestInvoiceModel extends Model {
  @attr('string', { defaultValue: null }) status!: GuestInvoiceStatus | null;
  @attr('string') externalId!: string;

  @belongsTo('payment', { async: false, inverse: 'guestInvoice' })
  payment!: ModelRegistry['payment'];

  @belongsTo('booking', { async: false, inverse: null })
  booking!: ModelRegistry['booking'];

  @hasMany('additional-booking-charge', { async: false, inverse: null })
  additionalBookingCharges!: ModelRegistry['additional-booking-charge'][];

  @cached
  get displayStatusKey(): string {
    switch (this.status) {
      case GuestInvoiceStatus.open:
      case GuestInvoiceStatus.paymentError:
      case GuestInvoiceStatus.paymentMethodMissing:
        return 'action_bookings_price.guest_invoice_status.open';
      case GuestInvoiceStatus.paid:
      case GuestInvoiceStatus.markedAsPaid:
        return 'action_bookings_price.guest_invoice_status.paid';
      default:
        return 'action_bookings_price.guest_invoice_status.void';
    }
  }

  @cached
  get isPaid(): boolean {
    return this.status === GuestInvoiceStatus.paid;
  }

  @cached
  get isMarkedAsPaid(): boolean {
    return this.status === GuestInvoiceStatus.markedAsPaid;
  }

  @cached
  get isVoid(): boolean {
    return this.status === GuestInvoiceStatus.void;
  }

  @cached
  get isUnpaid(): boolean {
    return !PAID_STATUSES.includes(this.status as GuestInvoiceStatus);
  }

  @cached
  get totalAmountWithTax(): number {
    return sum(this.additionalBookingCharges.map((item) => item.amountWithTax));
  }

  @cached
  get totalAmountWithoutTax(): number {
    return sum(
      this.additionalBookingCharges.map((item) => item.amountWithoutTax),
    );
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    'guest-invoice': GuestInvoiceModel;
  }
}
